import { CollectionConfig } from "@rebasepro/types";
import companiesCollection from "./companies";

const scrapedJobsCollection: CollectionConfig = {
    name: "Vacantes que encontramos fuera",
    singularName: "Vacante que encontramos fuera",
    slug: "scraped-jobs",
    table: "scraped_jobs",
    description: "Ofertas recogidas de fuentes externas, pendientes de revisión",
    // Internal scraping pipeline — staff-only. Rows here are unreviewed
    // third-party content and never reach an end user before promotion.
    securityRules: [
        {
            name: "admin_full_access",
            operation: "all",
            roles: ["admin"]
        }
    ],
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid"
        },
        external_id: {
            name: "Id que le puso la web de origen",
            type: "string",
            description: "ID único en la fuente (para evitar duplicados)"
        },
        source: {
            name: "Fuente",
            type: "string",
            enum: [
                { id: "jsearch", label: "JSearch", color: "blue" },
                { id: "serpapi", label: "SerpAPI", color: "purple" },
                { id: "arbeitnow", label: "Arbeitnow", color: "teal" },
                { id: "manual", label: "La cargamos a mano", color: "gray" }
            ]
        },
        title: {
            name: "Título",
            type: "string",
            validation: {
                required: true
            }
        },
        company_name: {
            name: "Empresa (texto)",
            type: "string",
            validation: {
                required: true
            }
        },
        location: {
            name: "Ubicación",
            type: "string"
        },
        location_city: {
            name: "Ciudad",
            type: "string"
        },
        location_country: {
            name: "País",
            type: "string"
        },
        description: {
            name: "Descripción",
            type: "string",
            admin: {
                multiline: true,
                // Markdown, not plain multiline: the sources hand over long
                // structured text — headings, bullet lists, the occasional
                // table — that a textarea renders as one unbroken wall. This
                // is the field a reviewer actually reads before approving, and
                // it is also what the LLM cleaner is handed, so whatever
                // structure survives here survives into the published job.
                markdown: true
            }
        },
        requirements: {
            name: "Requisitos",
            type: "string",
            admin: {
                multiline: true
            }
        },
        salary_range: {
            name: "Rango salarial",
            type: "string"
        },
        employment_type: {
            name: "Jornada",
            type: "string",
            description: "Horas, tal y como lo diga la oferta. Vacío = no lo dice."
        },
        contract_type: {
            name: "Tipo de contrato",
            type: "string",
            description: "Relación laboral, independiente de la jornada"
        },
        is_remote: {
            name: "Remoto",
            type: "boolean"
        },
        application_link: {
            name: "Enlace para aplicar",
            type: "string",
            description: "URL externa donde se postulan los candidatos"
        },
        source_url: {
            name: "URL del anuncio original",
            type: "string",
            description: "URL de la publicación original en la fuente"
        },
        employer_logo: {
            name: "Logo del empleador",
            type: "string",
            admin: {
                urlPreview: "image"
            }
        },
        employer_website: {
            name: "Web del empleador",
            type: "string",
            description: "Dominio web de la empresa según la fuente"
        },
        company_id: {
            name: "Empresa",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => companiesCollection,
                localKey: "company_id"
            },
            description: "Vínculo con los datos enriquecidos de la empresa"
        },
        posted_at: {
            name: "Publicada el",
            type: "date",
            description: "Cuándo se publicó originalmente en la fuente"
        },
        scraped_at: {
            name: "Capturada el",
            type: "date",
            autoValue: "on_create"
        },
        keywords_matched: {
            name: "Palabras clave",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Palabra clave" },
            description: "Palabras clave de sostenibilidad que coincidieron"
        },
        relevance_score: {
            name: "Palabras de sostenibilidad",
            type: "number",
            description: "0–1 según cuántas palabras de sostenibilidad aparecen en el anuncio. Lo cuenta el sistema al recogerla; no es una valoración del puesto."
        },
        search_query: {
            name: "Búsqueda",
            type: "string",
            description: "La búsqueda con la que se encontró esta oferta"
        },
        search_country: {
            name: "País de búsqueda",
            type: "string",
            description: "Código de país usado en la búsqueda"
        },
        status: {
            name: "Estado",
            type: "string",
            enum: [
                { id: "pending", label: "Por revisar", color: "orange" },
                { id: "approved", label: "Aprobada", color: "green" },
                { id: "rejected", label: "Rechazada", color: "red" },
                { id: "expired", label: "Caducada", color: "gray" }
            ],
            defaultValue: "pending"
        },
        reviewed_by: {
            name: "Revisada por",
            type: "string",
            userSelect: true,
            description: "Quien revisó esta oferta"
        },
        reviewed_at: {
            name: "Revisada el",
            type: "date"
        },
        // Set by the promotion step, not by hand. Pointing this at a different
        // job (or clearing it) silently breaks dedup — the scraper would
        // re-import an offer that is already live.
        //
        // Deliberately a plain id and not a `belongsTo`: `companies` already
        // imports this module, so importing `jobs` here closes a
        // companies → scraped_jobs → jobs → companies cycle that the CJS interop
        // resolves to `undefined` at registry-build time. The other direction —
        // reaching the scraped source from a job — is the one worth having, and
        // it costs no new edge.
        promoted_job_id: {
            name: "Vacante publicada",
            type: "string",
            description: "UUID de la vacante creada al aprobar esta oferta",
            admin: { readOnly: true }
        },
        raw_data: {
            name: "Datos sin procesar",
            type: "map",
            keyValue: true,
            description: "Respuesta completa sin procesar, para depuración",
            admin: { readOnly: true }
        }
    },
    admin: {
        group: "Vacantes",
        icon: "CloudDownload",
        inlineEditing: true,
        sort: ["scraped_at", "desc"],
        // 29 columns down to the five that decide whether an offer is worth
        // opening. `reviewed_by` and `posted_at` are null in every live row.
        listProperties: ["title", "company_name", "status", "relevance_score", "scraped_at"],
        previewProperties: ["title", "company_name", "status"],
        // Nothing in this table is authored by a human — it is scraper output
        // awaiting a verdict. The form is therefore organised for *reading*:
        // the offer first, its provenance and the raw payload collapsed.
        form: {
            sidebar: ["status", "relevance_score", "source", "scraped_at", "reviewed_at", "promoted_job_id"],
            sections: [
                {
                    key: "offer",
                    properties: ["title", "company_name", "location", "employment_type", "is_remote", "salary_range"]
                },
                {
                    key: "text",
                    title: "Texto de la oferta",
                    properties: ["description", "requirements"]
                },
                {
                    key: "links",
                    title: "Enlaces y empresa",
                    properties: ["source_url", "application_link", "employer_website", "employer_logo", "company_id"]
                },
                {
                    key: "provenance",
                    title: "De dónde salió",
                    collapsed: true,
                    properties: [
                        "external_id",
                        "search_query",
                        "search_country",
                        "keywords_matched",
                        "location_city",
                        "location_country",
                        "posted_at",
                        "reviewed_by",
                        "raw_data"
                    ]
                }
            ]
        },
        // The "Ofertas externas" workspace is the way in: it reads the same
        // rows and approving there runs the promotion endpoint, which writing
        // `status = approved` in this table does not. Two doors to the same
        // queue meant the wrong one got used. The table stays routable — the
        // workspace's pencil opens a row in the side panel — it just isn't
        // offered in the drawer any more.
        hideFromNavigation: true
    }
};

export default scrapedJobsCollection;
